import { Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'shared-lib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuComponent } from './menu/menu.component';
import { AppMobileFirstLayoutComponent } from "../../../shared-lib/src/lib/components/app-mobile-first-layout/app-mobile-first-layout.component";
import { BottomNavMenuComponent } from './bottom-nav-menu/bottom-nav-menu.component';
import { AppLayoutComponent, ResponsiveBaseComponent } from "shared-lib";
import { ParkerProvisioningService } from './auth/parker-provisioning.service';
import { JkuLayoutComponent } from './jku-webshop/layout/jku-layout.component';
import { JkuUserService } from './jku-webshop/jku-user.service';
import { isJkuHost } from './auth/login-options';

/** i18n key for the JKU tab title. */
const JkuPageTitleKey = 'JKU.PAGE_TITLE';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MenuComponent, AppMobileFirstLayoutComponent, BottomNavMenuComponent, AppLayoutComponent, JkuLayoutComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent extends ResponsiveBaseComponent {
  title = 'JJAMES Smart Parking';

  private readonly documentTitle = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly lang = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly parkerProvisioning = inject(ParkerProvisioningService);
  private readonly jkuUser = inject(JkuUserService);

  private readonly isJkuWebshop = signal(this.isJkuUrl(this.router.url));

  /**
   * JKU users get the JKU shell across the whole app, not just the webshop — JKU asked that the
   * site never switch between the JKU and the JJames look mid-session.
   *
   * The URL check is a safety net: the webshop route is SSO-guarded, but if a session ever loses
   * the `user_type` claim the sub-site must still render in its own layout rather than nesting
   * inside the JJames shell.
   */
  protected readonly useJkuShell = computed(() =>
    isJkuHost() || this.jkuUser.isJkuSso() || this.isJkuWebshop());

  /**
   * Tab title and favicon for the JKU hostname.
   *
   * index.html is shared by both hostnames — one Angular build, one Static Web App — so a JKU
   * visitor otherwise gets "JJames Parking System" and the JJames icon in their tab, their
   * bookmarks and their history. Swapping at runtime keeps that to one build instead of a second
   * deployment per brand.
   *
   * The title runs through i18n and re-reads on language switch; JKU's own pages are German first
   * and the shell offers a DE/EN toggle.
   */
  private applyBranding(): void {
    if (!isJkuHost()) {
      return;
    }

    const translated = this.translate.instant(JkuPageTitleKey);
    this.documentTitle.setTitle(translated === JkuPageTitleKey ? 'Parken an der JKU' : translated);

    // Vendored to assets/jku/favicon.ico from https://www.jku.at/favicon.ico rather than
    // hot-linked, so the tab icon does not depend on jku.at being reachable. Multi-resolution
    // ICO (48/32/16). jku.at publishes no SVG icon, which is why this is .ico and not .svg.
    //
    // Swap only once the file has actually loaded. Pointing the existing <link> at a missing asset
    // does not fall back to the previous icon — it leaves the tab with no icon at all, which is
    // what happened when this shipped ahead of the artwork.
    const jkuIcon = 'assets/jku/favicon.ico';
    const probe = new Image();
    probe.onload = () => {
      // Replace the element rather than mutate its href: browsers are inconsistent about
      // re-reading an icon <link> whose href changes after the original has already loaded,
      // and a stale JJames icon on a JKU page is the whole thing this is meant to prevent.
      document.querySelectorAll('link[rel~="icon"]').forEach(el => el.remove());
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      link.href = jkuIcon;
      document.head.appendChild(link);
    };
    probe.src = jkuIcon;
  }

  constructor() {
    super();

    // Re-runs on language switch; lang.current() is the tracked dependency.
    effect(() => {
      this.lang.current();
      this.applyBranding();
    });

    // Ensure a backend RegisteredParker exists for the logged-in user (esp. SSO users
    // who skip /parker/signup). Idempotent, fire-and-forget. Keycloak init has already
    // completed by the time the root component is constructed.
    this.parkerProvisioning.ensureParker();

    this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isJkuWebshop.set(this.isJkuUrl(event.urlAfterRedirects));
      }
    });
  }

  private isJkuUrl(url: string): boolean {
    return url.toLowerCase().startsWith('/jku-webshop');
  }
}
